import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { listAdminFeatureFlags } from '../api/adminOperations';
import { resolveAdminPermission } from '../constants/adminPermissionCatalog';

const DEFAULT_KEYS = Object.freeze(['ai_planner']);

const AdminFeatureFlagsContext = createContext({
  flags: {},
  loading: true,
  error: null,
  refresh: () => Promise.resolve(),
  isEnabled: () => false,
});

export function AdminFeatureFlagsProvider({ children, keys = DEFAULT_KEYS }) {
  const { user } = useAuth();
  const abortRef = useRef(null);
  const normalizedKeys = useMemo(
    () => (Array.isArray(keys) && keys.length ? keys : DEFAULT_KEYS).filter(Boolean),
    [keys]
  );
  const keysKey = normalizedKeys.join(',');

  const [state, setState] = useState({
    flags: {},
    loading: true,
    error: null,
    loadedAt: null,
  });

  const refresh = useCallback(async () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const data = await listAdminFeatureFlags(
        keysKey ? { keys: keysKey } : {},
        { signal: controller.signal }
      );
      setState({
        flags: data?.flags && typeof data.flags === 'object' ? data.flags : {},
        loading: false,
        error: null,
        loadedAt: Date.now(),
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      const status = Number(error?.status);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: status === 401 || status === 403 ? '権限がありません' : '取得に失敗しました',
      }));
    } finally {
      abortRef.current = null;
    }
  }, [keysKey]);

  const isLoggedIn = Boolean(user?.isLoggedIn);
  const canUseAiPlanner =
    isLoggedIn &&
    user?.userType === 'admin' &&
    String(user?.role || '').toLowerCase() !== 'task_only' &&
    resolveAdminPermission({
      permissionKey: 'NAV_AI_PLANNER',
      role: user?.role,
      permissions: user?.permissions,
      isExternalAccount: user?.is_external_account === true,
    });
  const requiresAiPlannerPermission = normalizedKeys.some((key) => key === 'ai_planner');
  const canAccessFlags =
    isLoggedIn &&
    user?.userType === 'admin' &&
    String(user?.role || '').toLowerCase() !== 'task_only' &&
    (!requiresAiPlannerPermission || canUseAiPlanner);

  useEffect(() => {
    if (!canAccessFlags) {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setState({ flags: {}, loading: false, error: null, loadedAt: null });
      return;
    }
    refresh();
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [canAccessFlags, refresh]);

  const value = useMemo(() => {
    const flags = state.flags && typeof state.flags === 'object' ? state.flags : {};
    return {
      flags,
      loading: state.loading,
      error: state.error,
      refresh,
      isEnabled: (key) => Boolean(key && flags[key]),
    };
  }, [state.flags, state.loading, state.error, refresh]);

  return (
    <AdminFeatureFlagsContext.Provider value={value}>
      {children}
    </AdminFeatureFlagsContext.Provider>
  );
}

export function useAdminFeatureFlags() {
  return useContext(AdminFeatureFlagsContext);
}
